// deps
import React, { memo, ReactElement, useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import {
    RouteProps,
    useEffectOnce,
    GenericError,
    GenericCrouton,
    format,
    AsyncComponent,
} from "@swiggy-private/core-ui";
import {
    GroupOrderCartContext,
    CheckoutRoute,
    CouponPageType,
    isJioHotstarFlow,
    recordCustomTrackingNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import {
    CouponTncHalfcard,
    EmptyCouponPage,
    getAppliedPaymentCouponCode,
    getAppliedRestaurantCouponCode,
} from "@swiggy-private/cart-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { ApplyCouponInputBox, CouponListingShimmer, ApplyingCouponBottomSheet } from "@swiggy-private/core-ui";

// components
import { CouponUIWrapper } from "./coupon-ui-wrapper";
import { MxNRenderer } from "./MxNRenderer";

//dls
import { Svg22 as Offer } from "@swiggy-private/dls-web-food-icons/dist/web/offersFilled";

// styles
import styles from "./coupon-ui-wrapper/CouponUIWrapper.scss";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

// helpers
import { isPaymentOfferCouponGroup } from "@cart-client/helpers/CouponListing";

// components
import { MROApplyCouponPopup } from "@swiggy-private/cart-ui";

// constants
import {
    COUPON_CROUTON_TEXT,
    CROUTON_IMG,
    EMPTY_COUPON_PAGE_SUBTITLE,
    EMPTY_COUPON_PAGE_TITLE,
} from "@cart-client/constants/Coupon";

// hooks
import { useGiftingCouponLogic } from "@cart-client/hooks/useGiftingCouponLogic";
import { useCouponState } from "./hooks/useCouponState";
import { useCouponAnalytics } from "./hooks/useCouponAnalytics";
import { useCouponHandlers } from "./hooks/useCouponHandlers";
import { useGiftingCouponAnalytics } from "./hooks/useGiftingCouponAnalytics";

// context
import { CouponRendererContext } from "@cart-client/contexts/CouponRendererContext";

// interfaces
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";

type CouponListingProps = RouteProps<CheckoutRoute>;

export const CouponListing = memo(function CouponListing(_props: CouponListingProps): ReactElement | null {
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    // read base theme from parent ThemeProvider
    const theme = useTheme();

    const {
        cartResponse,
        cartTotal,
        restaurantId,
        cartId,
        couponCode,
        setCouponCode,
        showCrouton,
        setShowCrouton,
        isPaymentCouponListingPage,
        paymentOffersRef,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        coupons,
        nextBestCoupon,
        reloadCouponListing,
        tncCoupon,
        paymentOfferCoupons,
        showBottomSheet,
        isCouponListingEmpty,
        appliedGiftingCoupons,
        isGiftingCouponListing,
        cards,
        childCartCount,
        applyCouponClashPopup,
        hasApplyCouponInitiated,
    } = useCouponState();

    const croutonTitle =
        typeof paymentOfferCoupons === "object" && "title" in paymentOfferCoupons
            ? format(COUPON_CROUTON_TEXT, String(paymentOfferCoupons?.title || ""))
            : "";
    const couponGroupType =
        typeof paymentOfferCoupons === "object" && "type" in paymentOfferCoupons
            ? paymentOfferCoupons?.type
            : undefined;
    const analytics = useCouponAnalytics(groupCartState, cartId);
    const giftingAnalytics = useGiftingCouponAnalytics({
        restaurantId: cartResponse?.data?.restaurant_details?.id,
        originalCartId: (cartResponse as GiftingReviewedCart)?.data?.originalCartId,
        cartId: (cartResponse as GiftingReviewedCart)?.data?.cartId,
    });

    const handlers = useCouponHandlers({
        croutonTitle,
        couponListingType: isPaymentCouponListingPage.current
            ? CouponPageType.PAYMENT_COUPON_LISTING
            : CouponPageType.RESTAURANT_COUPON_LISTING,
        isPaymentCouponListingPage,
        cartResponse,
        paymentOffersRef,
        setShowCrouton,
        analytics,
    });

    const [isCouponInputFocused, setIsCouponInputFocused] = useState(false);
    const onCouponInputFocus = useCallback(() => setIsCouponInputFocused(true), []);
    const onCouponInputBlur = useCallback(() => {
        setIsCouponInputFocused(false);
        // JioHotstar flow to bring back the crouton when the user blurs the coupon input
        if (isJioHotstarFlow() && !isEmpty(paymentOfferCoupons)) {
            setShowCrouton(true);
        }
    }, [paymentOfferCoupons, setShowCrouton]);

    // Use gifting-specific logic hook
    const {
        updatedAppliedCouponCodes,
        isFooterDisabled,
        couponApplied,
        couponSelections,
        isCouponAvailable,
        updateGiftingCouponHandler,
        onCouponCheckboxClick,
        navigateToGiftingCart,
    } = useGiftingCouponLogic(coupons, giftingAnalytics, appliedGiftingCoupons);

    const applyCouponHandler = useMemo(
        () => (isGiftingCouponListing ? updateGiftingCouponHandler : handlers.applyCouponHandler),
        [isGiftingCouponListing, updateGiftingCouponHandler, handlers],
    );

    useEffectOnce(() => {
        const sid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);

        // will remove after a month once debugging is over
        recordCustomTrackingNREvent({
            action: "CouponListing",
            timestamp: Date.now(),
            sid,
        });
    });

    useEffectOnce(() => {
        if (!isGiftingCouponListing) {
            analytics.handleImpressionOffers(groupCartState?.data?.group_cart_id || "");
        }

        const appliedCouponCode = isPaymentCouponListingPage.current
            ? getAppliedPaymentCouponCode(cartResponse?.data)
            : getAppliedRestaurantCouponCode(cartResponse?.data);

        fetchCoupons({
            restaurantId,
            cartValue: cartTotal,
            couponCode: appliedCouponCode,
            appliedCouponCodes: appliedGiftingCoupons,
            subpage: isPaymentCouponListingPage.current
                ? CouponPageType.PAYMENT_COUPON_LISTING
                : CouponPageType.RESTAURANT_COUPON_LISTING,
            childCartCount,
        });
    });

    const couponRendererContextValue = useMemo(
        () => ({
            paymentOffersRef,
            updateGiftingCouponHandler,
            couponSelections,
            onCouponCheckboxClick,
            appliedCouponCodes: updatedAppliedCouponCodes,
            isFooterDisabled,
            isCouponAvailable,
            couponApplied,
            coupons,
            isPaymentCouponListingPage,
            handlers,
            isGiftingCouponListing,
            giftingAnalytics,
        }),
        [
            paymentOffersRef,
            updateGiftingCouponHandler,
            couponSelections,
            onCouponCheckboxClick,
            updatedAppliedCouponCodes,
            isFooterDisabled,
            isCouponAvailable,
            couponApplied,
            coupons,
            isPaymentCouponListingPage,
            handlers,
            isGiftingCouponListing,
            giftingAnalytics,
        ],
    );

    if (isCouponListingEmpty) {
        if (isGiftingCouponListing) {
            return (
                <CouponUIWrapper isLoading={isFetching} isGiftingCartEmpty>
                    <EmptyCouponPage onCTAClick={navigateToGiftingCart} />
                </CouponUIWrapper>
            );
        }

        return (
            <CouponUIWrapper
                cartTotal={cartTotal.toString()}
                isLoading={isFetching}
                isPaymentCouponListingPage={isPaymentCouponListingPage.current}
                isGiftingCartEmpty
            >
                <GenericError
                    pageImpressionEvent={analytics.handleImpressionErrorPage}
                    title={EMPTY_COUPON_PAGE_TITLE}
                    message={EMPTY_COUPON_PAGE_SUBTITLE}
                    primaryAction={null}
                    action={null}
                    contentClassName={styles.couponPageErrorWithHeader}
                />
            </CouponUIWrapper>
        );
    }

    return (
        <CouponUIWrapper
            cartTotal={cartTotal.toString()}
            isLoading={isFetching}
            isPaymentCouponListingPage={isPaymentCouponListingPage.current}
        >
            <ApplyCouponInputBox
                ref={isPaymentOfferCouponGroup(couponGroupType) ? paymentOffersRef : null}
                isLoading={isFetching}
                onApplyClick={applyCouponHandler}
                couponCode={couponCode}
                setCouponCode={setCouponCode}
                handleSearchBarImpression={giftingAnalytics.handleSearchBarImpression}
                isGiftingCouponListing={isGiftingCouponListing}
                onFocus={onCouponInputFocus}
                onBlur={onCouponInputBlur}
            />

            {isFetching ? (
                <CouponListingShimmer />
            ) : (
                <CouponRendererContext.Provider value={couponRendererContextValue}>
                    <AsyncComponent
                        placeholder={CouponListingShimmer}
                        resolve={MxNRenderer}
                        cards={cards}
                        theme={theme}
                    />
                </CouponRendererContext.Provider>
            )}

            {showBottomSheet && (
                <ApplyingCouponBottomSheet
                    loading={isApplyingCoupon || !!isRemovingCoupon}
                    isRemovingCoupon={isRemovingCoupon}
                    closeBottomSheet={handlers.closeBottomSheet}
                    reloadCouponListing={reloadCouponListing}
                    errorMessage={errorMessage}
                    nextBestCoupon={nextBestCoupon}
                    onApplyCouponClick={applyCouponHandler}
                    showTncCouponHalfcard={handlers.showTncCouponHalfcard}
                    clickMoreDetailsEvent={analytics.handleClickMoreDetails}
                    isPaymentListingCouponCard={isPaymentCouponListingPage.current}
                    onErrorHalfCardImpression={analytics.handleImpressionHalfCardInvalidCoupon}
                />
            )}

            {!isEmpty(applyCouponClashPopup) && applyCouponClashPopup?.enabled && hasApplyCouponInitiated && (
                <MROApplyCouponPopup {...applyCouponClashPopup} />
            )}

            {showCrouton && (!isJioHotstarFlow() || !isCouponInputFocused) && (
                <GenericCrouton
                    title={croutonTitle}
                    leftIcon={{ icon: Offer, fillColor: "primary", strokeColor: "text_inverse_Highest_Emphasis" }}
                    postTextIcon={CROUTON_IMG}
                    onClick={handlers.onCroutonOfferClick}
                    buttonClassName={styles.croutonButton}
                    setCroutonVisibility={setShowCrouton}
                    wrapperClassName={styles.croutonWrapper}
                    impressionEvent={() => analytics.handleImpressionOfferCrouton(croutonTitle, cartResponse)}
                />
            )}
            {!isEmpty(tncCoupon) && (
                <CouponTncHalfcard couponData={tncCoupon} handleClose={handlers.closeTncCouponHalfcard} />
            )}
        </CouponUIWrapper>
    );
});
